import { 
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  ISignUpResult,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import configuredConfig from '../config/cognito-config';

// Initialize the Cognito User Pool
const userPool = new CognitoUserPool({
  UserPoolId: configuredConfig.userPoolId,
  ClientId: configuredConfig.clientId
});

interface SignUpParams {
  username: string;
  password: string;
  email: string;
  phone_number?: string;
}

interface SignInParams {
  username: string;
  password: string;
}

interface SignInResult {
  isSignedIn: boolean;
  nextStep: any;
}

class AuthService {
  // Sign up a new user
  async signUp({ username, password, email, phone_number }: SignUpParams): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        const attributeList = [];
        
        // Add email attribute
        const emailAttribute = new CognitoUserAttribute({
          Name: 'email',
          Value: email
        });
        attributeList.push(emailAttribute);
        
        // Add phone number attribute if provided
        if (phone_number) {
          const phoneAttribute = new CognitoUserAttribute({
            Name: 'phone_number',
            Value: phone_number
          });
          attributeList.push(phoneAttribute);
        }
        
        userPool.signUp(username, password, attributeList, [], (err, result) => {
          if (err) {
            // Error signing up
            reject(err);
            return;
          }
          
          resolve({
            isSignUpComplete: result?.userConfirmed,
            userId: result?.userSub,
            nextStep: 'CONFIRM_SIGN_UP'
          });
        });
      } catch (error) {
        // Error in signUp
        reject(error);
      }
    });
  }
  
  // Confirm sign up with verification code
  async confirmSignUp(username: string, code: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: userPool
        });
        
        cognitoUser.confirmRegistration(code, true, (err, result) => {
          if (err) {
            // Error confirming sign up
            reject(err);
            return;
          }
          
          resolve(true);
        });
      } catch (error) {
        // Error in confirmSignUp
        reject(error);
      }
    });
  }

  // Sign in user
  async signIn({ username, password }: SignInParams): Promise<SignInResult> {
    return new Promise((resolve, reject) => {
      try {
        const authenticationDetails = new AuthenticationDetails({
          Username: username,
          Password: password
        });

        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: userPool
        });

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (session) => {
            // Store tokens in localStorage
            localStorage.setItem('accessToken', session.getAccessToken().getJwtToken());
            localStorage.setItem('idToken', session.getIdToken().getJwtToken());
            localStorage.setItem('refreshToken', session.getRefreshToken().getToken());
            
            // Extract user info from ID token
            const idToken = session.getIdToken().getJwtToken();
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            
            // Store user data
            const userData = {
              email: payload.email,
              sub: payload.sub,
              cognitoUsername: payload['cognito:username'],
              userId: payload.sub,
              firstName: payload.given_name || '',
              lastName: payload.family_name || '',
              role: payload['cognito:groups']?.includes('Admins') ? 'ADMIN' : 'USER',
              profileImage: payload.picture || undefined
            };
            
            localStorage.setItem('userData', JSON.stringify(userData));
            
            resolve({ 
              isSignedIn: true, 
              nextStep: null 
            });
          },
          onFailure: (err) => {
            // Error signing in
            reject(err);
          },
          newPasswordRequired: (userAttributes, requiredAttributes) => {
            // Handle new password required challenge
            resolve({ 
              isSignedIn: false, 
              nextStep: { 
                signInStep: 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED',
                signInChallengeType: 'NEW_PASSWORD_REQUIRED' 
              } 
            });
          }
        });
      } catch (error) {
        // Error signing in
        reject(error);
      }
    });
  }

  // Sign out user
  async signOut(): Promise<boolean> {
    try {
      const currentUser = userPool.getCurrentUser();
      if (currentUser) {
        currentUser.signOut();
      }
      
      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('idToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userData');
      
      return true;
    } catch (error) {
      // Error signing out
      throw error;
    }
  }

  // Get current authenticated user
  async getCurrentUser(): Promise<CognitoUser | null> {
    try {
      const cognitoUser = userPool.getCurrentUser();
      if (!cognitoUser) {
        return null;
      }
      
      return new Promise((resolve, reject) => {
        cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
          if (err) {
            // Error getting session
            reject(err);
            return;
          }
          
          if (session && session.isValid()) {
            resolve(cognitoUser);
          } else {
            resolve(null);
          }
        });
      });
    } catch (error) {
      // Error getting current user
      return null;
    }
  }
  
  // Reset password
  async forgotPassword(username: string): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: userPool
        });
        
        cognitoUser.forgotPassword({
          onSuccess: (data) => {
            resolve({ nextStep: 'CONFIRM_FORGOT_PASSWORD' });
          },
          onFailure: (err) => {
            // Error initiating password reset
            reject(err);
          }
        });
      } catch (error) {
        // Error in forgotPassword
        reject(error);
      }
    });
  }
  
  // Confirm new password with verification code
  async forgotPasswordSubmit(username: string, code: string, newPassword: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        const cognitoUser = new CognitoUser({
          Username: username,
          Pool: userPool
        });
        
        cognitoUser.confirmPassword(code, newPassword, {
          onSuccess: () => {
            resolve(true);
          },
          onFailure: (err) => {
            // Error submitting new password
            reject(err);
          }
        });
      } catch (error) {
        // Error in forgotPasswordSubmit
        reject(error);
      }
    });
  }
  
}

export const authService = new AuthService();